import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { useLanguage } from '../../contexts/LanguageContext';
import SEOHead from '../../components/SEOHead';
import ProfessionalLayout from '../../components/ProfessionalLayout';
import ShipmentMonitoring from '../../components/ShipmentMonitoring';
import FlightAPITester from '../../components/FlightAPITester';
import CompanySettingsManager from '../../components/CompanySettingsManager';
import GeographicManagement from '../../components/GeographicManagement';
import PackageSearch from '../../components/PackageSearch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Building2, Users, UserPlus, UserX, Upload,
  Package, Ship, Settings, AlertCircle, Clock,
  Phone, Mail, MapPin, Shield, Ban, CheckCircle, Send, Plane, Search, RefreshCw, X
} from 'lucide-react';
import '../../styles/professional-theme.css';

const SuperAdminDashboard = () => {
  const { t, language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selectedUser, setSelectedUser] = useState(null);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);

  const [suspensionData, setSuspensionData] = useState({
    reason: '',
    duration_days: ''
  });

  // Contacts import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [cleaningContacts, setCleaningContacts] = useState(false);
  const [contactSearch, setContactSearch] = useState('');

  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    role: 'partner',
    phone: '',
    country: '',
    city: '',
    assigned_zone_id: '',
    assigned_countries: [],
    assigned_cities: [],
    is_country_wide: false
  });

  const [geographicZones, setGeographicZones] = useState([]);
  const [createdUserInfo, setCreatedUserInfo] = useState(null);
  const [countriesData, setCountriesData] = useState(null);
  const [selectedCountryCities, setSelectedCountryCities] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [showEmailTestDialog, setShowEmailTestDialog] = useState(false);
  const [emailTestData, setEmailTestData] = useState({
    to_email: '',
    subject: '',
    content: ''
  });
  const [emailTestLoading, setEmailTestLoading] = useState(false);
  const [operationalIndicators, setOperationalIndicators] = useState(null);
  const [searchModalOpen, setSearchModalOpen] = useState(false);

  useEffect(() => {
    fetchData();
    fetchCountriesData();
    fetchEmailStatus();
    fetchGeographicZones();
    fetchOperationalIndicators();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/api/companies/management');
      setData(response.data);
    } catch (err) {
      console.error('Error fetching management data:', err);
      setError(t('errorLoadingData'));
    } finally {
      setLoading(false);
    }
  };

  const FALLBACK_COUNTRIES = {
    "Cameroun": ["Douala", "Yaoundé", "Bafoussam", "Bamenda", "Kribi", "Limbé", "Garoua", "Maroua", "Bertoua", "Ebolowa"],
    "Côte d'Ivoire": ["Abidjan", "Yamoussoukro", "Bouaké", "San-Pédro"],
    "Sénégal": ["Dakar", "Saint-Louis"],
    "Gabon": ["Libreville", "Port-Gentil"],
    "Congo": ["Brazzaville", "Pointe-Noire"],
    "France": ["Paris", "Lyon", "Marseille", "Toulouse", "Nice", "Nantes", "Strasbourg", "Montpellier", "Bordeaux", "Lille"],
    "Allemagne": ["Berlin", "Munich", "Hambourg", "Cologne", "Francfort", "Stuttgart"],
    "Belgique": ["Bruxelles", "Anvers", "Gand", "Charleroi", "Liège", "Bruges"],
    "Royaume-Uni": ["Londres", "Birmingham", "Manchester", "Glasgow", "Liverpool"],
    "Espagne": ["Madrid", "Barcelone", "Valence", "Séville"],
    "Italie": ["Rome", "Milan", "Naples", "Turin"],
    "Suisse": ["Zurich", "Genève", "Bâle", "Berne", "Lausanne"],
    "Chine": ["Guangzhou", "Shanghai", "Beijing", "Shenzhen", "Yiwu"],
    "États-Unis": ["New York", "Los Angeles", "Chicago"],
    "Canada": ["Montréal", "Toronto"],
  };

  const fetchCountriesData = async () => {
    try {
      const response = await api.get('/api/countries/list');
      setCountriesData(response.data);
    } catch (err) {
      console.error('Error fetching countries:', err);
      setCountriesData({
        countries: Object.keys(FALLBACK_COUNTRIES).sort(),
        countries_with_cities: FALLBACK_COUNTRIES
      });
    }
  };

  const fetchEmailStatus = async () => {
    try {
      const response = await api.get('/api/email/status');
      setEmailStatus(response.data);
    } catch (err) {
      console.error('Error fetching email status:', err);
    }
  };

  const fetchGeographicZones = async () => {
    try {
      const response = await api.get('/api/geographic/zones');
      setGeographicZones(response.data.geographic_zones || []);
    } catch (err) {
      console.error('Error fetching geographic zones:', err);
    }
  };

  const fetchOperationalIndicators = async () => {
    try {
      const response = await api.get('/api/analytics/operational-indicators');
      setOperationalIndicators(response.data);
    } catch (err) {
      console.error('Error fetching operational indicators:', err);
    }
  };

  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    setEmailTestLoading(true);
    setError('');

    try {
      await api.post('/api/email/test', emailTestData);
      setSuccess(t('testEmailSuccess'));
      setShowEmailTestDialog(false);
      setEmailTestData({ to_email: '', subject: '', content: '' });
    } catch (err) {
      console.error('Error sending test email:', err);
      setError(err.response?.data?.detail || t('errorSendingEmail'));
    } finally {
      setEmailTestLoading(false);
    }
  };

  const handleSuspendUser = async () => {
    if (!selectedUser) return;

    try {
      await api.post(`/api/users/${selectedUser.id}/suspend`, {
        user_id: selectedUser.id,
        reason: suspensionData.reason,
        duration_days: suspensionData.duration_days ? parseInt(suspensionData.duration_days) : null
      });

      setSuccess(`${selectedUser.name || selectedUser.email} ${t('userSuspended')}`);
      setShowSuspendDialog(false);
      setSuspensionData({ reason: '', duration_days: '' });
      setSelectedUser(null);
      fetchData();
    } catch (err) {
      console.error('Error suspending user:', err);
      setError(err.response?.data?.detail || t('errorSuspending'));
    }
  };

  const handleUnsuspendUser = async (userId, userName) => {
    try {
      await api.post(`/api/users/${userId}/unsuspend`, {});
      setSuccess(`${userName} ${t('userReactivated')}`);
      fetchData();
    } catch (err) {
      console.error('Error unsuspending user:', err);
      setError(err.response?.data?.detail || t('errorUnsuspending'));
    }
  };

  const handleResendInvitation = async (userId, name) => {
    setError('');
    try {
      const res = await api.post(`/api/admin/partners/${userId}/resend-invitation`);
      if (res.data.email_sent) {
        setSuccess(`${name} — ${t('invitationResent')}`);
      } else {
        setError(`${t('invitationNotSentUseLink')} : ${res.data.activation_url}`);
      }
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || t('errorAddingUser'));
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await api.post('/api/admin/create-partner', {
        email: newUserData.email,
        full_name: newUserData.name,
        role: newUserData.role,
        country: newUserData.country,
        city: newUserData.city,
        phone: newUserData.phone,
        assigned_zone_id: newUserData.assigned_zone_id,
        assigned_countries: newUserData.assigned_countries,
        assigned_cities: newUserData.assigned_cities,
        is_country_wide: newUserData.is_country_wide
      });

      setCreatedUserInfo(response.data);
      setSuccess(`${newUserData.name} — ${t('userCreatedSuccess')}`);

      setNewUserData({
        name: '', email: '', role: 'partner', phone: '', country: '', city: '',
        assigned_zone_id: '', assigned_countries: [], assigned_cities: [], is_country_wide: false
      });
      setSelectedCountryCities([]);
      fetchData();
    } catch (err) {
      console.error('Error adding user:', err);
      setError(err.response?.data?.detail || t('errorAddingUser'));
    }
  };

  const handleCountryChange = (country) => {
    setNewUserData({...newUserData, country: country, city: ''});
    if (countriesData && countriesData.countries_with_cities[country]) {
      setSelectedCountryCities(countriesData.countries_with_cities[country]);
    } else {
      setSelectedCountryCities([]);
    }
  };

  const fetchContacts = async (search = '') => {
    setContactsLoading(true);
    try {
      const params = search ? { search } : {};
      const response = await api.get('/api/contacts', { params });
      setContacts(response.data.contacts || []);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setContactsLoading(false);
    }
  };

  const handleCleanContacts = async () => {
    setCleaningContacts(true);
    setError('');
    try {
      const res = await api.post('/api/contacts/clean');
      const { groups_merged = 0, contacts_removed = 0, total_after = 0 } = res.data || {};
      setSuccess(`Nettoyage terminé : ${contacts_removed} doublon(s) fusionné(s) sur ${groups_merged} numéro(s). ${total_after} contacts restants.`);
      fetchContacts(contactSearch);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du nettoyage');
    } finally {
      setCleaningContacts(false);
    }
  };

  const handleImportClients = async () => {
    if (!importFile) return;
    setImporting(true);
    setError('');
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await api.post('/api/clients/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(response.data);
      setSuccess(`${response.data.imported} contacts importés avec succès`);
      setImportFile(null);
      fetchContacts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'platform_admin': return 'bg-red-200 text-red-900';
      case 'super_admin': return 'bg-red-100 text-red-800';
      case 'company_admin': return 'bg-muted text-foreground';
      case 'country_admin': return 'bg-accent/10 text-accent';
      case 'partner': return 'bg-green-100 text-green-800';
      case 'local_partner': return 'bg-accent/10 text-accent';
      case 'client': return 'bg-muted text-foreground';
      default: return 'bg-muted text-foreground';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'platform_admin': return t('platformAdminRole') || 'Platform Admin';
      case 'super_admin': return 'Super Admin';
      case 'company_admin': return t('companyAdminRole');
      case 'country_admin': return t('countryAdminRole');
      case 'partner': return t('collectPartner');
      case 'local_partner': return t('deliveryPartner');
      case 'client': return t('client');
      default: return role;
    }
  };

  const locale = language === 'en' ? 'en-GB' : 'fr-FR';

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-lg text-muted-foreground">{t('loadingData')}</p>
        </div>
      </ProfessionalLayout>
    );
  }

  if (!data) {
    return (
      <ProfessionalLayout>
        <Alert variant="destructive">
          <AlertDescription>{t('unableToLoadData')}</AlertDescription>
        </Alert>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout>
      <SEOHead title={t('seoDashboardTitle')} description={t('seoDashboardDesc')} noindex />
      <div className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="bg-green-50 border-green-200">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {/* Company Info */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className="h-6 w-6 text-accent" />
                <div>
                  <CardTitle>{data.company.name}</CardTitle>
                  <CardDescription>Code: {data.company.code} • {data.company.country}</CardDescription>
                </div>
              </div>
              <Badge className={data.company.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {data.company.status === 'active' ? t('active') : t('inactive')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{data.company.contact_email}</span>
              </div>
              {data.company.contact_phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{data.company.contact_phone}</span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{t('createdOn')} {new Date(data.company.created_at).toLocaleDateString(locale)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Package Search Button */}
        <div className="flex justify-end mb-4">
          <Button onClick={() => setSearchModalOpen(true)} className="gap-2">
            <Search className="h-4 w-4" />
            {t('searchPackage')}
          </Button>
        </div>

        {/* Operational Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="analytics-kpi">
            <div className="analytics-kpi-header">
              <span className="analytics-kpi-label">{t('pendingPackages')}</span>
              <div className="analytics-kpi-icon" style={{ background: 'rgb(232 89 12 / 0.1)', color: '#e8590c' }}>
                <Package size={18} />
              </div>
            </div>
            <div className="analytics-kpi-value">{operationalIndicators?.pending_packages?.total || 0}</div>
            <div className="analytics-kpi-delta">
              {operationalIndicators?.pending_packages?.total_weight || 0} kg · {t('unassigned')}
            </div>
          </div>

          <div className="analytics-kpi">
            <div className="analytics-kpi-header">
              <span className="analytics-kpi-label">{t('partnersInvolved')}</span>
              <div className="analytics-kpi-icon" style={{ background: 'rgb(26 26 26 / 0.08)', color: '#1a1a1a' }}>
                <Users size={18} />
              </div>
            </div>
            <div className="analytics-kpi-value">{operationalIndicators?.partners_involved?.count || 0}</div>
            <div className="analytics-kpi-delta">
              {operationalIndicators?.partners_involved?.partners?.slice(0, 2).map(p => p.partner_name).join(', ') || t('withPendingPackages')}
            </div>
          </div>

          <div className="analytics-kpi">
            <div className="analytics-kpi-header">
              <span className="analytics-kpi-label">{t('topDestinations')}</span>
              <div className="analytics-kpi-icon" style={{ background: 'rgb(16 185 129 / 0.1)', color: '#10b981' }}>
                <MapPin size={18} />
              </div>
            </div>
            <div className="analytics-kpi-value">{operationalIndicators?.top_destinations?.length || 0}</div>
            <div className="analytics-kpi-delta">
              {operationalIndicators?.top_destinations?.slice(0, 3).map(d => d.country).join(', ') || t('destinationCountries')}
            </div>
          </div>

          <div className={`analytics-kpi ${
            operationalIndicators?.recommendation?.urgency === 'high' ? 'border-red-500 border-2' :
            operationalIndicators?.recommendation?.urgency === 'medium' ? 'border-orange-500 border-2' : ''
          }`} style={{ borderStyle: operationalIndicators?.recommendation?.urgency === 'high' || operationalIndicators?.recommendation?.urgency === 'medium' ? 'solid' : undefined }}>
            <div className="analytics-kpi-header">
              <span className="analytics-kpi-label">{t('recommendation')}</span>
              <div className="analytics-kpi-icon" style={{
                background: operationalIndicators?.recommendation?.urgency === 'high' ? 'rgb(239 68 68 / 0.1)' :
                  operationalIndicators?.recommendation?.urgency === 'medium' ? 'rgb(249 115 22 / 0.1)' : 'rgb(102 102 102 / 0.1)',
                color: operationalIndicators?.recommendation?.urgency === 'high' ? '#ef4444' :
                  operationalIndicators?.recommendation?.urgency === 'medium' ? '#f97316' : '#666'
              }}>
                <Plane size={18} />
              </div>
            </div>
            <div className="analytics-kpi-value" style={{ fontSize: 14, fontWeight: 600 }}>
              <Badge className={
                operationalIndicators?.recommendation?.urgency === 'high' ? 'bg-red-500' :
                operationalIndicators?.recommendation?.urgency === 'medium' ? 'bg-orange-500' :
                'bg-muted-foreground'
              }>
                {operationalIndicators?.recommendation?.urgency === 'high' ? t('urgent') :
                 operationalIndicators?.recommendation?.urgency === 'medium' ? t('moderate') :
                 t('wait')}
              </Badge>
            </div>
            <div className="analytics-kpi-delta" style={{ lineHeight: 1.4 }}>
              {operationalIndicators?.recommendation?.message || t('loadingData')}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="monitoring" className="w-full">
          <TabsList className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="monitoring">
              <Plane className="h-4 w-4 mr-2" />
              {t('shipmentMonitoring')}
            </TabsTrigger>
            <TabsTrigger value="flight-api">
              <Search className="h-4 w-4 mr-2" />
              {t('flightApi')}
            </TabsTrigger>
            <TabsTrigger value="users">{t('usersTab')}</TabsTrigger>
            <TabsTrigger value="emails">{t('emailsTab')}</TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-2" />
              {t('companySettings')}
            </TabsTrigger>
            <TabsTrigger value="geographic">
              <MapPin className="h-4 w-4 mr-2" />
              {t('geographicManagement')}
            </TabsTrigger>
            <TabsTrigger value="clients" onClick={() => { if (contacts.length === 0) fetchContacts(); }}>
              <Users className="h-4 w-4 mr-2" />
              Clients
            </TabsTrigger>
          </TabsList>

          <TabsContent value="monitoring" className="space-y-4">
            <ShipmentMonitoring />
          </TabsContent>

          <TabsContent value="flight-api" className="space-y-4">
            <FlightAPITester />
          </TabsContent>

          <TabsContent value="users" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">{t('userManagement')}</h3>
              <Button onClick={() => setShowAddUserDialog(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                {t('addUser')}
              </Button>
            </div>

            {data.users_by_role && Object.entries(data.users_by_role).map(([role, users]) => (
              <Card key={role}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Badge className={getRoleColor(role)}>
                      {getRoleLabel(role)}
                    </Badge>
                    <span className="text-sm text-muted-foreground">({users?.length || 0})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {users && Array.isArray(users) && users.map(user => user && (
                      <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-muted-foreground">
                              {user.name ? user.name.charAt(0).toUpperCase() : user.email?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{user.name || user.full_name || user.email}</p>
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                            {user.phone && (
                              <p className="text-xs text-muted-foreground">{user.phone}</p>
                            )}
                            {user.suspended_at && (
                              <div className="flex items-center gap-1 text-xs text-red-600">
                                <Ban className="h-3 w-3" />
                                {t('suspended')}: {user.suspension_reason}
                              </div>
                            )}
                            {user.must_set_password && (
                              <div className="flex items-center gap-1 text-xs text-blue-600">
                                <Clock className="h-3 w-3" />
                                {t('pendingActivation')}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            {user.is_active ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <Ban className="h-4 w-4 text-red-500" />
                            )}
                          </div>

                          {user.role !== 'super_admin' && user.id !== data.company.created_by && (
                            <div className="flex gap-1">
                              {user.must_set_password && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  title={t('resendInvitation')}
                                  onClick={() => handleResendInvitation(user.id, user.name || user.email)}
                                >
                                  <Send className="h-3 w-3" />
                                </Button>
                              )}
                              {user.is_active ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setShowSuspendDialog(true);
                                  }}
                                >
                                  <UserX className="h-3 w-3" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUnsuspendUser(user.id, user.name || user.email)}
                                >
                                  <CheckCircle className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="emails" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">{t('emailNotifications')}</h3>
              <Button
                onClick={() => setShowEmailTestDialog(true)}
                disabled={!emailStatus?.enabled}
              >
                <Send className="h-4 w-4 mr-2" />
                {t('testEmail')}
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  {t('emailServiceStatus')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">{t('service')}:</span>
                    <Badge>{emailStatus?.service || 'N/A'}</Badge>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">{t('state')}:</span>
                    <Badge className={emailStatus?.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {emailStatus?.enabled ? t('enabled') : t('disabled')}
                    </Badge>
                  </div>

                  {emailStatus?.sender_email && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium">{t('senderLabel')}:</span>
                      <span className="text-sm">{emailStatus.sender_email}</span>
                    </div>
                  )}

                  {!emailStatus?.enabled && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        {t('emailNotConfigured')}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('emailNotifTypes')}</CardTitle>
                <CardDescription>{t('emailNotifDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    t('emailOnCreation'),
                    t('emailOnCollected'),
                    t('emailOnTransit'),
                    t('emailOnArrived'),
                    t('emailOnReady'),
                    t('emailOnDelivered'),
                  ].map((label, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">{label}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <CompanySettingsManager />
          </TabsContent>

          <TabsContent value="geographic">
            <GeographicManagement />
          </TabsContent>

          <TabsContent value="clients" className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <h3 className="text-lg font-semibold">Carnet de clients</h3>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => fetchContacts(contactSearch)} disabled={contactsLoading}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${contactsLoading ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
                <Button size="sm" variant="outline" onClick={handleCleanContacts} disabled={cleaningContacts}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${cleaningContacts ? 'animate-spin' : ''}`} />
                  Nettoyer les doublons
                </Button>
                <Button size="sm" onClick={() => { setShowImportDialog(true); setImportResult(null); setImportFile(null); }}>
                  <Upload className="h-4 w-4 mr-1" />
                  Importer
                </Button>
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={contactSearch}
                onChange={(e) => {
                  setContactSearch(e.target.value);
                  if (e.target.value.length >= 2 || e.target.value === '') fetchContacts(e.target.value);
                }}
                placeholder="Rechercher un client (nom, téléphone, email)..."
                className="w-full pl-9 pr-8 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
              {contactSearch && (
                <button onClick={() => { setContactSearch(''); fetchContacts(''); }} className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </div>

            {contactsLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : contacts.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Aucun client importé</p>
                    <p className="text-sm text-muted-foreground mt-1">Importez un fichier CSV ou Excel pour commencer</p>
                    <Button className="mt-4" onClick={() => { setShowImportDialog(true); setImportResult(null); setImportFile(null); }}>
                      <Upload className="h-4 w-4 mr-1" />
                      Importer des clients
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground mb-3">{contacts.length} contacts</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground">Nom</th>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground">Téléphone</th>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden sm:table-cell">Email</th>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden md:table-cell">Ville</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {contacts.map(c => (
                          <tr key={c.id} className="hover:bg-muted/30">
                            <td className="px-3 py-2 font-medium">{c.name}</td>
                            <td className="px-3 py-2">{c.phone}</td>
                            <td className="px-3 py-2 hidden sm:table-cell text-muted-foreground">{c.email || '—'}</td>
                            <td className="px-3 py-2 hidden md:table-cell text-muted-foreground">{c.city || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Import Clients Dialog */}
        {showImportDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-card p-4 sm:p-6 rounded-lg w-full max-w-[calc(100vw-2rem)] sm:max-w-md">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Upload className="h-5 w-5 text-accent" />
                Importer des clients
              </h3>

              <div className="space-y-4">
                <div className="p-3 bg-muted/50 border border-border rounded text-sm">
                  <p className="font-medium mb-1">Format attendu :</p>
                  <p className="text-muted-foreground">Fichier CSV ou Excel avec au minimum les colonnes :</p>
                  <p className="font-mono text-xs mt-1 bg-muted p-2 rounded">Nom | Téléphone | Email (optionnel) | Pays (optionnel) | Ville (optionnel)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Fichier (.csv ou .xlsx)
                  </label>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => {
                      setImportFile(e.target.files?.[0] || null);
                      setImportResult(null);
                    }}
                    className="w-full p-2 border border-border rounded-md text-sm"
                  />
                  {importFile && (
                    <p className="text-xs text-muted-foreground mt-1">{importFile.name} ({(importFile.size / 1024).toFixed(1)} Ko)</p>
                  )}
                </div>

                {importResult && (
                  <div className={`p-3 rounded border text-sm ${importResult.imported > 0 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    <p className="font-medium">{importResult.imported} importés, {importResult.skipped} ignorés sur {importResult.total_rows} lignes</p>
                    {importResult.errors?.length > 0 && (
                      <ul className="mt-2 text-xs text-red-600 space-y-1">
                        {importResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                      </ul>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowImportDialog(false)}>
                    {importResult ? 'Fermer' : 'Annuler'}
                  </Button>
                  {!importResult && (
                    <Button
                      onClick={handleImportClients}
                      disabled={!importFile || importing}
                      className="bg-accent hover:bg-accent/90"
                    >
                      {importing ? (
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-1" />
                      )}
                      Importer
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Suspend Dialog */}
        {showSuspendDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-card p-6 rounded-lg w-96">
              <h3 className="text-lg font-semibold mb-4">
                {t('suspendUser')} {selectedUser?.name}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    {t('suspensionReason')}
                  </label>
                  <textarea
                    value={suspensionData.reason}
                    onChange={(e) => setSuspensionData({...suspensionData, reason: e.target.value})}
                    className="w-full p-2 border border-border rounded-md"
                    rows={3}
                    placeholder={t('explainReason')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    {t('durationDays')}
                  </label>
                  <input
                    type="number"
                    value={suspensionData.duration_days}
                    onChange={(e) => setSuspensionData({...suspensionData, duration_days: e.target.value})}
                    className="w-full p-2 border border-border rounded-md"
                    placeholder="30"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSuspendDialog(false);
                    setSelectedUser(null);
                    setSuspensionData({ reason: '', duration_days: '' });
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleSuspendUser}
                  disabled={!suspensionData.reason.trim()}
                >
                  {t('suspendUser')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add User Dialog */}
        {showAddUserDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-card p-6 rounded-lg w-96 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">{t('addUserTitle')}</h3>

              <form onSubmit={handleAddUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">{t('name')}</label>
                  <input
                    type="text"
                    required
                    value={newUserData.name}
                    onChange={(e) => setNewUserData({...newUserData, name: e.target.value})}
                    className="w-full p-2 border border-border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">{t('email')}</label>
                  <input
                    type="email"
                    required
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                    className="w-full p-2 border border-border rounded-md"
                  />
                </div>

                <div className="bg-accent/10 p-3 rounded-md">
                  <div className="text-sm text-accent">
                    <strong>{t('autoPasswordLabel')}</strong><br />
                    {t('autoPassword')}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">{t('role')}</label>
                  <select
                    value={newUserData.role}
                    onChange={(e) => setNewUserData({...newUserData, role: e.target.value})}
                    className="w-full p-2 border border-border rounded-md"
                  >
                    <option value="partner">{t('collectPartner')}</option>
                    <option value="local_partner">{t('deliveryPartner')}</option>
                    <option value="country_admin">{t('countryAdminRole')}</option>
                    <option value="company_admin">{t('companyAdminRole')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">{t('phone')}</label>
                  <input
                    type="tel"
                    value={newUserData.phone}
                    onChange={(e) => setNewUserData({...newUserData, phone: e.target.value})}
                    className="w-full p-2 border border-border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    {t('country')} {!newUserData.country && data?.company?.country && (
                      <span className="text-xs text-muted-foreground">({t('useCompanyCountry')}: {data.company.country})</span>
                    )}
                  </label>
                  <select
                    value={newUserData.country}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    className="w-full p-2 border border-border rounded-md"
                  >
                    <option value="">{t('useCompanyCountry')} ({data?.company?.country || t('notDefined')})</option>
                    {countriesData && countriesData.countries && countriesData.countries.map(country => (
                      <option key={country || ''} value={country || ''}>{country || t('unknownCountry')}</option>
                    ))}
                  </select>
                </div>

                {selectedCountryCities.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">{t('city')}</label>
                    <select
                      value={newUserData.city}
                      onChange={(e) => setNewUserData({...newUserData, city: e.target.value})}
                      className="w-full p-2 border border-border rounded-md"
                    >
                      <option value="">{t('selectCity')}</option>
                      {selectedCountryCities.map(city => (
                        <option key={city || ''} value={city || ''}>{city || t('unknownCity')}</option>
                      ))}
                    </select>
                  </div>
                )}

                {newUserData.role === 'local_partner' && (
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-semibold mb-3 text-accent">{t('geoAssignment')}</h4>
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">{t('assignedZone')}</label>
                      <select
                        value={newUserData.assigned_zone_id}
                        onChange={(e) => {
                          const zone = geographicZones && geographicZones.find(z => z.zone_id === e.target.value);
                          if (zone) {
                            setNewUserData({
                              ...newUserData,
                              assigned_zone_id: e.target.value,
                              assigned_countries: zone.country_code ? [zone.country_code] : [],
                              assigned_cities: zone.cities || [],
                              is_country_wide: zone.is_country_wide || false
                            });
                          }
                        }}
                        className="w-full p-2 border border-border rounded-md"
                      >
                        <option value="">{t('selectZone')}</option>
                        {geographicZones && geographicZones.map(zone => (
                          <option key={zone.zone_id} value={zone.zone_id}>
                            {zone.zone_name} ({zone.country_code})
                          </option>
                        ))}
                      </select>
                    </div>

                    {newUserData.assigned_zone_id && (
                      <div className="mt-2 text-sm text-muted-foreground bg-accent/10 p-2 rounded">
                        <strong>{t('selectedZone')}</strong><br />
                        {t('countriesLabel')}: {newUserData.assigned_countries ? newUserData.assigned_countries.join(', ') : t('none')}<br />
                        {t('citiesLabel')}: {newUserData.assigned_cities && newUserData.assigned_cities.length > 0 ? newUserData.assigned_cities.join(', ') : t('allCities')}
                      </div>
                    )}
                  </div>
                )}

                {createdUserInfo && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                    <h4 className="font-semibold text-green-800 mb-2">{t('userCreatedSuccess')}</h4>
                    <div className="text-sm text-green-700">
                      <div><strong>{t('email')} :</strong> {createdUserInfo.email}</div>
                      <div><strong>{t('role')} :</strong> {createdUserInfo.role}</div>
                      {createdUserInfo.email_sent ? (
                        <div className="text-xs text-muted-foreground mt-2">
                          {t('invitationSent')}
                        </div>
                      ) : (
                        <div className="mt-3">
                          <div className="text-xs text-yellow-700 mb-1">{t('invitationNotSentUseLink')}</div>
                          <div className="flex gap-2 items-center">
                            <input
                              readOnly
                              value={createdUserInfo.activation_url || ''}
                              onFocus={(e) => e.target.select()}
                              className="flex-1 text-xs px-2 py-1 border rounded bg-white text-foreground"
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => navigator.clipboard?.writeText(createdUserInfo.activation_url || '')}
                            >
                              {t('copy')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddUserDialog(false);
                      setNewUserData({
                        name: '', email: '', role: 'partner', phone: '', country: '', city: '',
                        assigned_zone_id: '', assigned_countries: [], assigned_cities: [], is_country_wide: false
                      });
                      setSelectedCountryCities([]);
                      setCreatedUserInfo(null);
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button type="submit">
                    <UserPlus className="h-4 w-4 mr-2" />
                    {t('createUser')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Test Email Dialog */}
        {showEmailTestDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-card p-6 rounded-lg w-96">
              <h3 className="text-lg font-semibold mb-4">{t('testEmailTitle')}</h3>

              <form onSubmit={handleSendTestEmail} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">{t('recipientEmail')}</label>
                  <input
                    type="email"
                    required
                    value={emailTestData.to_email}
                    onChange={(e) => setEmailTestData({...emailTestData, to_email: e.target.value})}
                    className="w-full p-2 border border-border rounded-md"
                    placeholder="test@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">{t('subject')}</label>
                  <input
                    type="text"
                    value={emailTestData.subject}
                    onChange={(e) => setEmailTestData({...emailTestData, subject: e.target.value})}
                    className="w-full p-2 border border-border rounded-md"
                    placeholder="Test Email FretManager"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">{t('message')}</label>
                  <textarea
                    value={emailTestData.content}
                    onChange={(e) => setEmailTestData({...emailTestData, content: e.target.value})}
                    className="w-full p-2 border border-border rounded-md"
                    rows="4"
                  />
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowEmailTestDialog(false);
                      setEmailTestData({ to_email: '', subject: '', content: '' });
                    }}
                    disabled={emailTestLoading}
                  >
                    {t('cancel')}
                  </Button>
                  <Button type="submit" disabled={emailTestLoading}>
                    {emailTestLoading ? t('sending') : t('send')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        <PackageSearch open={searchModalOpen} onOpenChange={setSearchModalOpen} />
      </div>
    </ProfessionalLayout>
  );
};

export default SuperAdminDashboard;
